import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Merge, Phone, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { V } from "@/lib/vocab";

// iter376 — duplicate residents left by a surname-only first import + a full-name re-import
// ("Agrawala" next to "Ashutosh Agrawala"). Backend proposes merges inside one home; the
// manager unticks anything that's really two people, then confirms.
export function MergeDuplicatesDialog({ tenantId, open, onClose, onMerged }) {
  const [groups, setGroups] = useState(null);
  const [checked, setChecked] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setGroups(null);
    api.get(`/tenants/${tenantId}/roster/duplicates`).then((r) => {
      setGroups(r.data.groups || []);
      setChecked(Object.fromEntries((r.data.groups || []).map((g) => [g.keep.id, true])));
    }).catch(() => { toast.error("Couldn't scan for duplicates"); onClose(); });
  }, [open, tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = (groups || []).filter((g) => checked[g.keep.id]);
  const removing = selected.reduce((n, g) => n + g.drop.length, 0);

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/roster/duplicates/merge`, {
        merges: selected.map((g) => ({ keep_id: g.keep.id, drop_ids: g.drop.map((d) => d.id) })),
      });
      toast.success(`Merged ${r.data.merged} ${V.lc("person")}${r.data.merged === 1 ? "" : "s"} · ${r.data.people_removed} duplicate row${r.data.people_removed === 1 ? "" : "s"} removed`);
      onMerged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setBusy(false); }
  };

  const Person = ({ p, result }) => (
    <div className={`rounded-xl px-3 py-2 text-sm ${result ? "bg-emerald-50 border border-emerald-200" : "bg-sand-50 border border-sand-200"}`}>
      <div className="font-semibold text-ink truncate">{p.name || <span className="text-ink/40">No name</span>}</div>
      <div className="text-xs text-ink/60 flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
        {p.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{p.phone}{result && p.other_phones?.length ? <span className="text-ink/40"> primary</span> : null}</span>}
        {p.other_phones?.map((x) => <span key={x} className="inline-flex items-center gap-1 text-ink/50"><Phone className="h-3 w-3" />{x}{result && <span className="text-ink/40"> also</span>}</span>)}
        {p.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{p.email}</span>}
        {p.emails?.map((x) => <span key={x} className="inline-flex items-center gap-1 truncate text-ink/50"><Mail className="h-3 w-3" />{x}</span>)}
        {p.consent && <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> consented</span>}
        {!p.phone && !p.email && !p.other_phones?.length && <span className="text-ink/40">no phone or email</span>}
      </div>
    </div>
  );
  const unitLabel = (g) => homeLabel(g) || "Home";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent data-testid="merge-dupes-dialog" className="max-w-3xl w-[calc(100vw-2rem)] max-h-[calc(100dvh-2rem)] flex flex-col p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-sand-200 text-left">
          <DialogTitle className="font-heading text-lg flex items-center gap-2"><Merge className="h-4 w-4 text-redline-600" /> Merge duplicate {V.lcs("person")}</DialogTitle>
          <DialogDescription>
            Rows in the <span className="font-medium text-ink">same property</span> that look like one person — same phone, same email, or a last-name-only row next to the full name. Left: the rows being combined. Right: the single {V.lc("person")} you'll have afterwards — nothing is dropped, extra numbers and emails are kept as alternates. Untick anything that is really two people.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3 bg-white">
          {groups === null && <div data-testid="merge-dupes-loading" className="flex items-center gap-2 text-sm text-ink/60 py-8 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Scanning the roster…</div>}
          {groups && groups.length === 0 && <div data-testid="merge-dupes-empty" className="text-sm text-ink/60 py-10 text-center">No duplicates found — every property's {V.lcs("person")} look distinct.</div>}
          {groups?.map((g) => (
            <label key={g.keep.id} data-testid="merge-dupes-group" className={`block rounded-2xl border p-3 cursor-pointer transition-colors ${checked[g.keep.id] ? "border-redline-200 bg-redline-50/30" : "border-sand-200 opacity-70"}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" data-testid="merge-dupes-check" className="mt-1 h-4 w-4 accent-[#e5342e]" checked={!!checked[g.keep.id]}
                  onChange={(e) => setChecked((c) => ({ ...c, [g.keep.id]: e.target.checked }))} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-ink/60 mb-2">
                    <span className="font-semibold text-ink">{unitLabel(g)}</span>
                    {g.unit && g.address && <span>· {g.address}</span>}
                    {g.reasons.map((r) => <span key={r} className="rounded-full bg-sand-100 px-2 py-0.5 text-[11px] font-medium text-ink/70">{r}</span>)}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-stretch gap-2">
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-ink/40 px-1">Combining {g.drop.length + 1} rows</div>
                      {[...g.drop, g.keep].map((d) => <Person key={d.id} p={d} />)}
                    </div>
                    <ArrowRight className="h-4 w-4 text-ink/40 mx-auto hidden md:block self-center" />
                    <div className="space-y-1.5">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 px-1">After merge — 1 {V.lc("person")}</div>
                      <Person p={g.result || g.keep} result />
                    </div>
                  </div>
                </div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-sand-200 bg-sand-50/60 flex-row items-center justify-between gap-3 sm:justify-between">
          <div data-testid="merge-dupes-summary" className="text-xs text-ink/60">{groups ? `${selected.length} of ${groups.length} merge${groups.length === 1 ? "" : "s"} selected · ${removing} row${removing === 1 ? "" : "s"} will be removed` : ""}</div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="merge-dupes-cancel" onClick={onClose} disabled={busy} className="rounded-full border border-sand-200 bg-white px-4 py-2 text-sm font-semibold hover:border-ink">Cancel</button>
            <button type="button" data-testid="merge-dupes-confirm" onClick={confirm} disabled={busy || !selected.length}
              className="rounded-full bg-redline-500 text-white px-5 py-2 text-sm font-semibold hover:bg-redline-600 disabled:opacity-50 inline-flex items-center gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Merge className="h-4 w-4" />} Merge {selected.length || ""}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
